/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { ThemeType } from "../DesignSystem/Theme/Theme";

interface QuickFormPaginationProps {
  theme: ThemeType;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const QuickFormPagination: React.FC<QuickFormPaginationProps> = ({
  theme,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = (): void => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = (): void => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        pages.push(pageNum);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push(-1); // Ellipsis
      }

      // Show current page and neighbors
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let pageNum = start; pageNum <= end; pageNum++) {
        if (!pages.includes(pageNum)) {
          pages.push(pageNum);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push(-2); // Ellipsis
      }

      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 30px",
        background: theme.colors.backgroundTertiary,
        borderTop: `1px solid ${theme.colors.dropdownBorderColor}`,
        fontFamily: theme.fonts.primary,
      }}
    >
      <div
        style={{
          fontSize: theme.fontSizes.medium,
          color: theme.colors.text,
        }}
      >
        Showing{" "}
        <strong style={{ color: theme.colors.secondary }}>
          {totalItems > 0 ? startItem : 0}–{endItem}
        </strong>{" "}
        of{" "}
        <strong style={{ color: theme.colors.secondary }}>{totalItems}</strong>{" "}
        files
      </div>

      <div
        style={{ display: "flex", gap: theme.spacing.sm, alignItems: "center" }}
      >
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          style={{
            minWidth: "36px",
            height: "36px",
            padding: `0 ${theme.spacing.md}`,
            border: `1px solid ${theme.colors.dropdownBorderColor}`,
            background: theme.colors.tableBackgroundColor,
            borderRadius: "6px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            fontSize: theme.fontSizes.medium,
            color: theme.colors.text,
            transition: "all 0.2s",
            opacity: currentPage === 1 ? 0.4 : 1,
            fontFamily: theme.fonts.primary,
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 1) {
              e.currentTarget.style.background =
                theme.colors.backgroundTertiary;
              e.currentTarget.style.borderColor = theme.colors.searchMainColor;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              theme.colors.tableBackgroundColor;
            e.currentTarget.style.borderColor =
              theme.colors.dropdownBorderColor;
          }}
        >
          ←
        </button>

        {getPageNumbers().map((page, index) => {
          if (page === -1 || page === -2) {
            return (
              <span
                key={`ellipsis-${index}`}
                style={{
                  padding: `0 ${theme.spacing.sm}`,
                  color: theme.colors.secondaryTextColor,
                }}
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              style={{
                minWidth: "36px",
                height: "36px",
                padding: `0 ${theme.spacing.md}`,
                border: `1px solid ${
                  page === currentPage
                    ? theme.colors.primaryButtonColor
                    : theme.colors.dropdownBorderColor
                }`,
                background:
                  page === currentPage
                    ? theme.colors.primaryButtonColor
                    : theme.colors.tableBackgroundColor,
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: theme.fontSizes.medium,
                color:
                  page === currentPage
                    ? theme.colors.primaryElementFontColor
                    : theme.colors.text,
                transition: "all 0.2s",
                fontFamily: theme.fonts.primary,
              }}
              onMouseEnter={(e) => {
                if (page !== currentPage) {
                  e.currentTarget.style.background =
                    theme.colors.backgroundTertiary;
                  e.currentTarget.style.borderColor =
                    theme.colors.searchMainColor;
                }
              }}
              onMouseLeave={(e) => {
                if (page !== currentPage) {
                  e.currentTarget.style.background =
                    theme.colors.tableBackgroundColor;
                  e.currentTarget.style.borderColor =
                    theme.colors.dropdownBorderColor;
                }
              }}
            >
              {page}
            </button>
          );
        })}

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            minWidth: "36px",
            height: "36px",
            padding: `0 ${theme.spacing.md}`,
            border: `1px solid ${theme.colors.dropdownBorderColor}`,
            background: theme.colors.tableBackgroundColor,
            borderRadius: "6px",
            cursor:
              currentPage === totalPages || totalPages === 0
                ? "not-allowed"
                : "pointer",
            fontSize: theme.fontSizes.medium,
            color: theme.colors.text,
            transition: "all 0.2s",
            opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1,
            fontFamily: theme.fonts.primary,
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages && totalPages !== 0) {
              e.currentTarget.style.background =
                theme.colors.backgroundTertiary;
              e.currentTarget.style.borderColor = theme.colors.searchMainColor;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              theme.colors.tableBackgroundColor;
            e.currentTarget.style.borderColor =
              theme.colors.dropdownBorderColor;
          }}
        >
          →
        </button>
      </div>
    </div>
  );
};
